import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';

import Breadcrumbs from '../components/BreadCrumbs';
import { FiPhone, FiTrendingUp, FiUsers, FiX } from 'react-icons/fi';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getLeadStats } from '../services/telecalling';
import CustomLoading from '../components/customLoading/CustomLoading';
import DateRangePicker from '../components/inputFields/DateRangePicker';
import { FaChartPie, FaChartBar, FaUserCircle, FaUserFriends } from 'react-icons/fa';

// Animated Circular Progress Bar Component
const CircularProgress = ({ percent, color, label }) => {
    const radius = 32;
    const stroke = 4;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (percent / 100) * circumference;
    return (
        <div className="flex flex-col items-center justify-center">
            <svg height={radius * 2} width={radius * 2}>
                <circle
                    stroke="#e5e7eb"
                    fill="transparent"
                    strokeWidth={stroke}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                />
                <circle
                    stroke={color}
                    fill="transparent"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={circumference + ' ' + circumference}
                    style={{ strokeDashoffset, transition: 'stroke-dashoffset 1s ease' }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                />
                <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dy="0.3em"
                    fontSize="13"
                    fill="#111827"
                    fontWeight="bold"
                >
                    {percent}%
                </text>
            </svg>
            <span className="text-xs mt-1 text-gray-600">{label}</span>
        </div>
    );
};

const LeadStatsOverview = ({ stats }) => {
    if (!stats) return null;

    // Prepare data for charts
    const categoryData = (stats.leads_by_category || []).map(item => ({
        name: item.category.replace("_", " "),
        value: item.count,
        percent: stats.total_leads ? Math.round((item.count / stats.total_leads) * 100) : 0
    }));

    const sourceData = (stats.leads_by_source || []).map(item => ({
        name: item.lead_source.replace("_", " "),
        value: item.count,
        percent: stats.total_leads ? Math.round((item.count / stats.total_leads) * 100) : 0
    }));

    const statusMap = {};
    (stats.leads_by_status || []).forEach(s => {
        statusMap[s.current_status] = s.count;
    });

    // Calculate conversion/contacted rates
    const conversionRate = stats.total_leads ? Math.round(((statusMap['converted'] || 0) / stats.total_leads) * 100) : 0;
    const contactedRate = stats.total_leads ? Math.round(((statusMap['contacted'] || 0) / stats.total_leads) * 100) : 0;

    const COLORS = ['#6366F1', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];

    return (
        <div className="gap-3 mb-6 grid md:grid-cols-3 grid-cols-1 bg-gradient-to-r from-indigo-50 via-pink-50 to-yellow-50 p-2 rounded-2xl">
            {/* Stats Cards - Compact Version */}
            <div className="grid grid-cols-2 gap-2">
                {/* <div className="col-span-2 flex flex-row gap-4 justify-center items-center mb-2">
                    <CircularProgress percent={conversionRate} color="#10B981" label="Conversion" />
                    <CircularProgress percent={contactedRate} color="#6366F1" label="Contacted" />
                </div> */}
                <CompactStatCard
                    title="Total Leads"
                    value={stats.total_leads}
                    icon={FiUsers}
                    iconColor="text-indigo-500"
                />
                <CompactStatCard
                    title="Contacted"
                    value={statusMap['contacted'] || 0}
                    icon={FiPhone}
                    iconColor="text-blue-500"
                />
                <CompactStatCard
                    title="Converted"
                    value={statusMap['converted'] || 0}
                    icon={FiTrendingUp}
                    iconColor="text-green-500"
                />
                <CompactStatCard
                    title="Pending"
                    value={statusMap['pending'] || 0}
                    icon={FiPhone}
                    iconColor="text-yellow-500"
                />
                <CompactStatCard
                    title="Interested"
                    value={statusMap['interested'] || 0}
                    icon={FiTrendingUp}
                    iconColor="text-pink-500"
                />
            </div>

            {/* Compact Pie Chart */}
            <div className="bg-white bg-gradient-to-br from-indigo-50 to-white p-3 rounded-xl shadow-md border border-gray-300">
                <div className="flex items-center gap-2 mb-2">
                    <FaChartPie className="text-indigo-400" />
                    <h3 className="text-sm font-semibold text-gray-800">By Category</h3>
                </div>
                <div className="flex items-center h-full">
                    <div className="w-40 h-40">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={categoryData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={45}
                                    outerRadius={60}
                                    paddingAngle={2}
                                    dataKey="value"
                                    isAnimationActive={true}
                                >
                                    {categoryData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    formatter={(value, name, props) => [
                                        value,
                                        `${name} (${props.payload.percent}%)`
                                    ]}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="ml-3 flex-1">
                        <div className="grid grid-cols-1 gap-2">
                            {categoryData.map((item, index) => (
                                <div key={index} className="flex items-center text-xs">
                                    <div
                                        className="w-2 h-2 rounded-full mr-1"
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="truncate">{item.name}</span>
                                    <span className="ml-1 font-medium">{item.percent}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Compact Bar Chart */}
            <div className="bg-white bg-gradient-to-br from-yellow-50 to-white p-3 rounded-xl shadow-md border border-gray-300">
                <div className="flex items-center gap-2 mb-2">
                    <FaChartBar className="text-yellow-400" />
                    <h3 className="text-sm font-semibold text-gray-800">By Source</h3>
                </div>
                <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={sourceData}
                            margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
                            barSize={20}
                            isAnimationActive={true}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 10 }}
                                tickFormatter={(value) => value}
                            />
                            <YAxis
                                tick={{ fontSize: 10 }}
                                width={25}
                            />
                            <Tooltip
                                formatter={(value, name, props) => [
                                    value,
                                    `${name} (${props.payload.percent}%)`
                                ]}
                            />
                            <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                                {sourceData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

// Compact Stat Card Component
const CompactStatCard = ({ title, value, icon: Icon, iconColor }) => (
    <div className="bg-white p-3 rounded-lg border border-gray-300 shadow-md flex flex-col items-start justify-between min-h-[80px]">
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
                <Icon className={`${iconColor} text-lg`} />
                <div>
                    <p className="text-xs font-medium text-gray-500">{title}</p>
                    <p className="text-lg font-bold text-gray-800">{value}</p>
                </div>
            </div>
        </div>
    </div>
);

// Staff Avatar (Initials in colored circle)
const StaffAvatar = ({ name }) => {
    const colors = ["bg-indigo-400", "bg-pink-400", "bg-yellow-400", "bg-green-400", "bg-blue-400"];
    const color = colors[(name.charCodeAt(0) + name.length) % colors.length];
    const initials = name.split(" ").map(n => n[0]).join("").toUpperCase();
    return (
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg shadow ${color}`}>
            {initials}
        </div>
    );
};

// Staff Performance Overview - now shows summary cards for each staff
const StaffPerformanceOverview = ({ staffStats }) => {
    if (!staffStats || staffStats.length === 0) return null;

    // Prepare data for the bar chart
    const chartData = staffStats.map(staff => ({
        name: staff.staff_name,
        totalLeads: staff.leads?.total_leads ?? 0,
        totalCalls: staff.calls?.total_calls ?? 0,
        assigned: staff.leads?.total_leads_assigned ?? 0,
        added: staff.leads?.total_leads_added ?? 0,
        pendingFollowUps: staff.follow_ups?.total_pending_follow_ups ?? 0,
    }));

    return (
        <>
            {/* Decorative Divider */}
            <div className="flex items-center gap-2 my-4">
                <FaUserFriends className="text-indigo-400 text-xl" />
                <div className="flex-1 border-t border-indigo-100" />
                <span className="text-xs text-indigo-400 font-semibold">Staff Performance</span>
                <div className="flex-1 border-t border-indigo-100" />
            </div>
            {/* Bar Graph for Staff Performance */}
            <div className="bg-white bg-gradient-to-br from-green-50 to-white rounded-xl p-4 shadow-md border border-gray-300 mb-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2"><FaChartBar className="text-green-400" /> Staff Performance Overview</h3>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            margin={{ top: 16, right: 16, left: 0, bottom: 16 }}
                            barCategoryGap={20}
                            isAnimationActive={true}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                            <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={-10} textAnchor="end" height={60} />
                            <YAxis tick={{ fontSize: 12 }} width={40} />
                            <Tooltip contentStyle={{ borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }} />
                            <Legend layout="horizontal" verticalAlign="top" align="center" wrapperStyle={{ paddingBottom: 10 }} />
                            <Bar dataKey="totalLeads" name="Total Leads" fill="#6366F1" radius={[4, 4, 0, 0]} barSize={18} />
                            <Bar dataKey="totalCalls" name="Total Calls" fill="#10B981" radius={[4, 4, 0, 0]} barSize={18} />
                            <Bar dataKey="assigned" name="Assigned" fill="#EC4899" radius={[4, 4, 0, 0]} barSize={18} />
                            <Bar dataKey="added" name="Added" fill="#3B82F6" radius={[4, 4, 0, 0]} barSize={18} />
                            <Bar dataKey="pendingFollowUps" name="Pending F/U" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={18} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Staff Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {staffStats.map((staff, idx) => (
                    <div key={idx} className="bg-white rounded-xl p-4 shadow-md border border-gray-300 flex flex-col gap-2 hover:shadow-lg transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                                <StaffAvatar name={staff.staff_name} />
                                <div>
                                    <h4 className="font-semibold text-gray-900 text-base">{staff.staff_name}</h4>
                                    <p className="text-xs text-gray-500">Staff ID: {staff.staff_id}</p>
                                </div>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                <span className="bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full font-semibold">Leads: {staff.leads?.total_leads ?? 0}</span>
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-semibold">Call Comments: {staff.calls?.total_calls ?? 0}</span>
                            </div>
                        </div>
                        <div className="flex flex-row gap-2 mb-2">
                            <span className="bg-pink-100 text-pink-800 text-xs px-2 py-1 rounded-full">Added: {staff.leads?.total_leads_added ?? 0}</span>
                            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Assigned: {staff.leads?.total_leads_assigned ?? 0}</span>
                            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">Pending F/U: {staff.follow_ups?.total_pending_follow_ups ?? 0}</span>
                        </div>
                        <div className="flex flex-row gap-2 mb-2">
                            <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">Talk Time: {staff.talk_time?.total_talk_time ?? '0 min'}</span>
                            <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full">Total Calls: {staff.calls?.total_calls ?? 0}</span>
                            {/* <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">Tracked: {staff.talk_time?.tracked_talk_time ?? '0 min'}</span>
                            <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">Untracked: {staff.talk_time?.untracked_talk_time ?? '0 min'}</span> */}
                        </div>
                        <div className="flex flex-row gap-2">
                            {/* <span className="bg-indigo-50 text-indigo-700 text-xs px-2 py-1 rounded-full">Tracked Calls: {staff.calls?.tracked_calls ?? 0}</span>
                            <span className="bg-red-50 text-red-700 text-xs px-2 py-1 rounded-full">Untracked Calls: {staff.calls?.untracked_calls ?? 0}</span> */}
                            <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full">Connected Calls: {staff.calls?.total_connected_calls ?? 0}</span>
                            <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full">Outgoing: {staff.calls?.total_outgoing_calls ?? 0}</span>
                            <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">Incoming: {staff.calls?.total_incoming_calls ?? 0}</span>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

function LeadStats() {
    const [statsData, setStatsData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [dateRange, setDateRange] = useState({ start: null, end: null });

    useEffect(() => {
        fetchStatsData({ query: "" });
    }, []);

    const handleDateRangeChange = (range) => {
        setDateRange(range);
        fetchStatsData({ query: `start_date=${range.start}&end_date=${range.end}` });
    };

    const fetchStatsData = async ({ query = null }) => {
        setIsLoading(true);
        try {
            let res = await getLeadStats(`?${query}`);
            if (res.status === 200) {
                setStatsData(res.data);
                setIsLoading(false);
            } else {
                toast.error("Unable to fetch leads stats");
            }
        } catch (error) {
            console.error("Error fetching lead stats:", error);
            toast.error("Error fetching leads stats.");
        }
    };

    return (
        <>
            <div className="my-2 flex flex-row justify-between items-center">
                <Breadcrumbs items={[{ name: 'Home', link: '/dashboard' }, { name: 'Leads' }, { name: 'Lead Stats' }]} />
                <div className="max-w-48 w-full">
                    <DateRangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        className="w-full text-sm"
                        compact
                    />
                </div>
            </div>

            {
                isLoading && <div className="flex justify-center items-center h-60">
                    <CustomLoading />
                </div>
            }
            {(!isLoading && statsData) && (
                <>
                    <LeadStatsOverview stats={statsData.general_stats} />
                    <StaffPerformanceOverview staffStats={statsData.staff_weekly_stats} />
                </>
            )}
        </>
    );
}

export default LeadStats;

